import styles from '../index.module.scss'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import CustomCell from './CustomCell'
import { IBookingsCell } from '../helper'
import { BookingTeacherStates } from '../../../../../firebase/types-bookings'
import { Box, Stack, styled, Typography, useTheme } from '@mui/material'
import pxToRem from '../../../../../helpers/pxToRem'
dayjs.extend(customParseFormat)

const StatusBox = styled(Box)({
    padding: `8px 12px`,
    width: 'fit-content',
    borderRadius: pxToRem(8),
})
const StatusStack = styled(Stack)({
    flexDirection: 'row',
    width: 'fit-content',
    alignItems: 'center',
    gap: pxToRem(12),
    '.MuiTypography-root': {
        color: styles.shades100,
    },
    // padding: `8px 12px`,
})

/**
 * Component that renders the state of the booking
 * For following cases, we do not need to fetch anything:
 * - If cancelled -> need to check if finding replacement (check replacementDetails/state)
 * - If not cancelled for today -> check teacherDetails/state
 * - If not cancelled for future dates -> confirmed
 * @param param0
 * @returns
 */

export const BookingStatusCellTypes = {
    ...BookingTeacherStates,
    findingReplacement: 'findingReplacement',
} as const
export type BookingStatusCellType =
    (typeof BookingStatusCellTypes)[keyof typeof BookingStatusCellTypes]

export const BookingStatusLabelMap: Record<BookingStatusCellType, string> = {
    cancelled: 'Cancelled',
    completed: 'Completed',
    confirmed: 'Confirmed',
    enRoute: 'En Route',
    findingReplacement: 'Finding Replacement',
    inProgress: 'In Progress',
    nearby: 'Nearby',
    earlyCheckout: 'Early Checkout',
} as const

const BookingStatusCell = ({
    value,
    setValue,
}: {
    value: BookingStatusCellType | null
} & IBookingsCell) => {
    const theme = useTheme()

    switch (value) {
        case 'confirmed': {
            return (
                <StatusBox
                    sx={{
                        backgroundColor: styles.neutrals50,
                    }}
                >
                    <Typography
                        variant="ps"
                        color={theme.palette.neutrals[600]}
                    >
                        Confirmed
                    </Typography>
                </StatusBox>
            )
        }
        case 'enRoute':
        case 'nearby': {
            return (
                <StatusBox
                    sx={{
                        backgroundColor: styles.secondaryBlue700,
                        borderRadius: pxToRem(40),
                    }}
                >
                    <Typography variant="ps" color={theme.palette.shades[0]}>
                        On the way!
                    </Typography>
                </StatusBox>
            )
        }
        case 'inProgress': {
            return (
                <StatusBox
                    sx={{
                        backgroundColor: styles.primaryPink800,
                    }}
                >
                    <Typography variant="ps" color={theme.palette.shades[0]}>
                        In Progress
                    </Typography>
                </StatusBox>
            )
        }
        case 'completed': {
            return (
                <StatusBox
                    sx={{
                        backgroundColor: styles.primaryGreen400,
                    }}
                >
                    <Typography variant="ps" color={theme.palette.shades[0]}>
                        Completed
                    </Typography>
                </StatusBox>
            )
        }
        case 'cancelled': {
            return (
                <StatusStack>
                    {/* prettier-ignore */}
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> <circle cx="12" cy="12" r="11" fill="#EC5B53" stroke="#FF625A" strokeWidth="2"/> <path d="M16.5 7.5L7.5 16.5M7.5 7.5L16.5 16.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/> </svg>
                    <Typography variant="ps">Cancelled</Typography>
                </StatusStack>
            )
        }
        case 'earlyCheckout': {
            return (
                <StatusBox
                    sx={{
                        backgroundColor: styles.shades100,
                    }}
                >
                    <Typography variant="ps" color={theme.palette.shades[0]}>
                        Early Checkout
                    </Typography>
                </StatusBox>
            )
        }
        case 'findingReplacement': {
            return (
                <StatusStack>
                    {/* prettier-ignore */}
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> <circle cx="12" cy="12" r="12" fill="#A54D9D"/> <path d="M14.6267 11.7573C15.7266 11.7573 16.6558 10.7829 16.6558 9.52232C16.6558 8.29185 15.7216 7.34766 14.6267 7.34766C13.5369 7.34766 12.5977 8.30692 12.6027 9.53237C12.6027 10.7829 13.5269 11.7573 14.6267 11.7573ZM8.82094 11.8728C9.7802 11.8728 10.5888 11.019 10.5888 9.92411C10.5888 8.84933 9.77016 8.03069 8.82094 8.03069C7.87172 8.03069 7.05308 8.8644 7.05811 9.93415C7.05811 11.019 7.8667 11.8728 8.82094 11.8728ZM6.05364 16.433H9.82038C9.28299 15.6646 9.86056 14.1479 10.9956 13.2539C10.4431 12.8973 9.72496 12.6362 8.82094 12.6362C6.57596 12.6362 5.12451 14.2885 5.12451 15.6496C5.12451 16.1468 5.37563 16.433 6.05364 16.433ZM11.4978 16.428H17.7556C18.5843 16.428 18.8706 16.1769 18.8706 15.7148C18.8706 14.4241 17.2283 12.6462 14.6267 12.6462C12.0202 12.6462 10.3829 14.4241 10.3829 15.7148C10.3829 16.1769 10.6692 16.428 11.4978 16.428Z" fill="white"/> </svg>
                    <Typography variant="ps" color={theme.palette.shades[0]}>
                        Finding Replacement
                    </Typography>
                </StatusStack>
            )
        }
        default: {
            return (
                <CustomCell loading={false} value={value || '-'} width={100} />
            )
        }
    }
}

export default BookingStatusCell
